import { useMemo, useContext, useEffect } from 'react';
import {
  useAdditionalFilterBarState,
  useAssetTypeState,
  useSearchBoxState,
  useSortByState,
  useDateAddedState,
  useImportSourceState,
  useAssetSourcesState,
  useImageSizeState,
  useVideoDurationState,
} from './components/state';
import {
  FilterContext,
} from './context/filter-context';
import {
  CSMLTimeFrame,
} from '../model';

export const useFilterBarState = ({
  logger,
  i18n,
  userPreference,
  defaultSearchText,
  defaultMinVideoDuration,
  defaultMaxVideoDuration,
}) => {
  const {
    setFilterBar,
  } = useContext(FilterContext);

  const {
    toggleFilterBar,
    showAdditionalFilterBar,
  } = useAdditionalFilterBarState({ logger });

  const {
    toggleAssetType,
    assetTypesState,
    assetTypes,
    isImageSelected,
    isVideoSelected,
  } = useAssetTypeState({ logger, i18n });

  const {
    searchText,
    onSearchTextChanging,
    onSearchTextBlur,
    onSearchTextChange,
    onSearchTextClear,
  } = useSearchBoxState({ logger, defaultSearchText });

  const {
    sortBy,
    sortingOptions,
    setSortBy,
    onSortByChange,
  } = useSortByState({ logger, i18n, userPreference });

  const {
    dateAddedChoiceOptions,
    dateAddedFilter,
    dateAddedFilterCustomDateRange,
    onDateAddedFilterChange,
  } = useDateAddedState({ logger, i18n });

  const {
    assetSources,
    assetSourceOptions,
    onAssetSourceFilterChange,
  } = useAssetSourcesState({ logger, i18n });

  const {
    importSource,
    importSourceOptions,
    onImportSourceFilterChange,
  } = useImportSourceState({ logger, i18n });

  const {
    minWidthFilter,
    minHeightFilter,
    onImageSizeFilterChange,
  } = useImageSizeState({ logger, i18n });

  const {
    minDurationFilter,
    maxDurationFilter,
    onVideoDurationFilterChange,
  } = useVideoDurationState({
    logger,
    i18n,
    defaultMinVideoDuration,
    defaultMaxVideoDuration,
  });

  const isDateAddedFilterActive = dateAddedFilter !== CSMLTimeFrame.Anytime;
  const isImportSourceFilterActive = importSource !== null;
  const isAssetSourceFilterActive = assetSources.length > 0;

  const isAdditionalFilterActive = useMemo(
    () => {
      if (isImageSelected) {
        if (minWidthFilter || minHeightFilter) {
          return true;
        }
      }
      if (isVideoSelected) {
        if (minDurationFilter || maxDurationFilter) {
          return true;
        }
      }
      return isDateAddedFilterActive || isImportSourceFilterActive || isAssetSourceFilterActive;
    },
    [isAssetSourceFilterActive,
      isDateAddedFilterActive,
      isImageSelected,
      isImportSourceFilterActive, isVideoSelected, maxDurationFilter, minDurationFilter, minHeightFilter, minWidthFilter]
  );

  useEffect(() => {
    setFilterBar({
      getFilters: () =>
        ({
          isImageSelected,
          isVideoSelected,
          assetTypesState,
          searchText,
          sortBy,
          dateAddedFilter,
          dateAddedFilterCustomDateRange,
          importSource,
          assetSources,
          minWidthFilter,
          minHeightFilter,
          minDurationFilter,
          maxDurationFilter,
        }),
    });
  }, [assetSources, assetTypesState, dateAddedFilter, dateAddedFilterCustomDateRange, importSource,
    isImageSelected, isVideoSelected, maxDurationFilter, minDurationFilter, minHeightFilter, minWidthFilter, searchText, setFilterBar, sortBy]);

  return {
    toggleFilterBar,
    showAdditionalFilterBar,
    isAdditionalFilterActive,
    toggleAssetType,
    isImageSelected,
    isVideoSelected,
    assetTypesState,
    assetTypes,
    // Search box state
    searchText,
    onSearchTextChanging,
    onSearchTextBlur,
    onSearchTextChange,
    onSearchTextClear,
    // Sort by state
    sortBy,
    sortingOptions,
    setSortBy,
    onSortByChange,
    // Date added state
    dateAddedChoiceOptions,
    dateAddedFilter,
    dateAddedFilterCustomDateRange,
    onDateAddedFilterChange,
    // Import source state
    importSource,
    importSourceOptions,
    onImportSourceFilterChange,
    // Asset source state
    assetSources,
    assetSourceOptions,
    onAssetSourceFilterChange,
    // Image size state
    minWidthFilter,
    minHeightFilter,
    onImageSizeFilterChange,
    // Video duration state
    minDurationFilter,
    maxDurationFilter,
    onVideoDurationFilterChange,
  };
};
